import React, { useState } from "react";
import { onBackdropMouseDown, onModalPanelMouseDown } from "../lib/modal";
import { Key, X, Eye, EyeOff } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";

/**
 * ChangePasswordButton
 * --------------------
 * A small icon button that lives in the top header next to the language
 * switcher and the bell. Any signed-in user (owner, manager, staff) can
 * change their OWN password — this is the recovery path the salon owner
 * was missing. The backend honours self-edits on PUT /api/users/{user_id}
 * (password-only changes are always permitted; role/branch/module fields
 * are silently dropped for non-privileged self-edits, see server.py).
 */
export default function ChangePasswordButton() {
  const { user, logout } = useAuth();
  const { lang } = useLanguage();
  const isAr = lang === "ar";

  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);

  if (!user?.user_id) return null;

  const reset = () => {
    setCurrent(""); setNext(""); setConfirm("");
    setShowPwd(false); setErr(""); setOk(false);
  };

  const close = () => { setOpen(false); reset(); };

  const submit = async (e) => {
    e?.preventDefault();
    setErr(""); setOk(false);

    // Basic client-side validation. We don't ask the backend to verify
    // the current password — re-login on success guarantees the user
    // is who they say they are (and our route is protected by the auth
    // middleware in any case).
    if (next.length < 6) {
      setErr(isAr ? "كلمة المرور قصيرة جداً (6 أحرف على الأقل)" : "Password too short (min 6 chars)");
      return;
    }
    if (next !== confirm) {
      setErr(isAr ? "كلمتا المرور غير متطابقتين" : "Passwords do not match");
      return;
    }
    setBusy(true);
    try {
      await api.put(`/users/${user.user_id}`, { password: next });
      setOk(true);
      // Give the user a beat to read the success message, then sign them
      // out so they re-authenticate with the new password.
      setTimeout(() => {
        close();
        logout();
      }, 1400);
    } catch (e2) {
      const msg = e2?.response?.data?.detail || (isAr ? "تعذّر تغيير كلمة المرور" : "Could not change password");
      setErr(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="change-password-btn"
        title={isAr ? "تغيير كلمة المرور" : "Change my password"}
        className="p-2 rounded-md border border-white/30 hover:border-white text-white/90 hover:text-white transition hidden sm:block"
      >
        <Key size={16} />
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onMouseDown={(e) => onBackdropMouseDown(e, close)}
        >
          <form
            onSubmit={submit}
            onMouseDown={onModalPanelMouseDown}
            className="w-full max-w-md rounded-xl bg-[var(--surface)] border border-[var(--border)] shadow-2xl p-6"
            data-testid="change-password-modal"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="font-heading text-xl text-[var(--text)] flex items-center gap-2">
                  <Key size={18} className="text-[var(--gold)]" />
                  {isAr ? "تغيير كلمة المرور" : "Change Password"}
                </h2>
                <p className="text-xs text-[var(--text-muted)] mt-1">
                  {isAr
                    ? `الحساب: ${user.username}`
                    : `Account: ${user.username}`}
                </p>
              </div>
              <button type="button" onClick={close} className="p-1 text-[var(--text-muted)] hover:text-[var(--text)]" aria-label="close">
                <X size={18} />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-xs text-[var(--text-muted)] mb-1 block">
                  {isAr ? "كلمة المرور الجديدة" : "New password"}
                </label>
                <div className="relative">
                  <input
                    type={showPwd ? "text" : "password"}
                    className="input-dark pe-9"
                    value={next}
                    onChange={(e) => setNext(e.target.value)}
                    autoComplete="new-password"
                    data-testid="cp-new-pwd"
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((v) => !v)}
                    className="absolute top-1/2 -translate-y-1/2 end-2 p-1 text-[var(--text-muted)] hover:text-[var(--text)]"
                    tabIndex={-1}
                  >
                    {showPwd ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="text-xs text-[var(--text-muted)] mb-1 block">
                  {isAr ? "تأكيد كلمة المرور" : "Confirm password"}
                </label>
                <input
                  type={showPwd ? "text" : "password"}
                  className="input-dark"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  autoComplete="new-password"
                  data-testid="cp-confirm-pwd"
                  required
                />
              </div>

              {err && (
                <div className="text-xs text-rose-500 bg-rose-500/10 border border-rose-500/30 rounded-md px-3 py-2" data-testid="cp-error">
                  {err}
                </div>
              )}
              {ok && (
                <div className="text-xs text-emerald-500 bg-emerald-500/10 border border-emerald-500/30 rounded-md px-3 py-2" data-testid="cp-success">
                  {isAr ? "تم تغيير كلمة المرور — سيتم تسجيل خروجك الآن" : "Password changed — signing you out now"}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 mt-5 pt-4 border-t border-[var(--border-subtle)]">
              <button
                type="button"
                onClick={close}
                disabled={busy}
                className="px-4 py-2 rounded-md text-sm text-[var(--text-muted)] hover:text-[var(--text)] disabled:opacity-50"
              >
                {isAr ? "إلغاء" : "Cancel"}
              </button>
              <button
                type="submit"
                disabled={busy || !next || !confirm}
                className="px-4 py-2 rounded-md text-sm bg-[var(--gold)] text-black font-semibold hover:opacity-90 disabled:opacity-50"
                data-testid="cp-submit"
              >
                {busy ? (isAr ? "..." : "...") : (isAr ? "حفظ" : "Save")}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
